import { useEffect, useMemo, useState } from 'react';
import type { ActionProviderId, Effort, ImpactType, Level, LinkType, Meta, Project, Status, Task } from '../types';
import { STATUSES, STATUS_LABELS } from '../types';
import { formatTimestamp } from '../lib/format';
import { WhyPanel } from './Score';
import { ProjectSelect } from './ProjectSelect';

export interface DocProviderOption {
  id: ActionProviderId;
  label: string;
}

export interface TaskDraft {
  title: string;
  description: string;
  dueDate: string;
  status: Status;
  priority: string;
  importance: string;
  effort: string;
  urgencyOverride: string;
  impactType: string;
  impactScope: string;
  blockingOthers: boolean;
  blockingNote: string;
  commitmentTo: string;
  commitmentDue: string;
  projectId: string;
}

interface Props {
  task: Task | null;
  meta: Meta | null;
  projects: Project[];
  onCreateProject: (name: string) => Promise<Project | undefined>;
  linkCandidates: Task[];
  saving: boolean;
  error: string | null;
  warnings: string[];
  onClose: () => void;
  onSave: (draft: TaskDraft) => void;
  onDelete?: () => void;
  onTogglePin?: () => void;
  onSnooze?: (until: string | null) => void;
  onAddLink?: (toTaskId: string, type: LinkType, syncEnabled: boolean) => void;
  onRemoveLink?: (linkId: string) => void;
  docProviders?: DocProviderOption[];
  onCreateArtifact?: (provider: ActionProviderId, shareWithOrg: boolean) => void;
}

function toDraft(task: Task | null): TaskDraft {
  return {
    title: task?.title ?? '',
    description: task?.description ?? '',
    dueDate: task?.dueDate ?? '',
    status: task?.status ?? 'todo',
    priority: task?.priority != null ? String(task.priority) : '',
    importance: task?.importance != null ? String(task.importance) : '',
    effort: task?.effort ?? '',
    urgencyOverride: task?.urgencyOverride != null ? String(task.urgencyOverride) : '',
    impactType: task?.impactType ?? '',
    impactScope: task?.impactScope != null ? String(task.impactScope) : '',
    blockingOthers: task?.blockingOthers ?? false,
    blockingNote: task?.blockingNote ?? '',
    commitmentTo: task?.commitmentTo ?? '',
    commitmentDue: task?.commitmentDue ?? '',
    projectId: task?.projectId ?? '',
  };
}

const LINK_VERBS: Record<LinkType, { outgoing: string; incoming: string }> = {
  blocks: { outgoing: 'blocks', incoming: 'is waiting on' },
  relates_to: { outgoing: 'relates to', incoming: 'relates to' },
  duplicates: { outgoing: 'duplicates', incoming: 'is duplicated by' },
};

/** A date N days from today, for the snooze buttons. */
function inDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export function TaskEditor({
  task,
  meta,
  projects,
  onCreateProject,
  linkCandidates,
  saving,
  error,
  warnings,
  onClose,
  onSave,
  onDelete,
  onTogglePin,
  onSnooze,
  onAddLink,
  onRemoveLink,
  docProviders = [],
  onCreateArtifact,
}: Props) {
  const [draft, setDraft] = useState<TaskDraft>(() => toDraft(task));
  const [linkTarget, setLinkTarget] = useState('');
  const [linkType, setLinkType] = useState<LinkType>('blocks');
  const [linkSync, setLinkSync] = useState(false);
  const [docProviderChoice, setDocProviderChoice] = useState<ActionProviderId | ''>(docProviders[0]?.id ?? '');
  const [shareWithOrg, setShareWithOrg] = useState(false);
  const isLinked = Boolean(task?.connectionId);

  useEffect(() => {
    if (docProviders.length && !docProviders.some((p) => p.id === docProviderChoice)) {
      setDocProviderChoice(docProviders[0].id);
    }
  }, [docProviders, docProviderChoice]);

  useEffect(() => setDraft(toDraft(task)), [task]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = <K extends keyof TaskDraft>(key: K, value: TaskDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const candidates = useMemo(
    () =>
      linkCandidates
        .filter((c) => c.id !== task?.id && !task?.links.some((l) => l.otherTaskId === c.id))
        .slice(0, 200),
    [linkCandidates, task]
  );

  const derivedTag = (source: string | null) =>
    source === 'derived' ? <span className="derived-tag">from source</span> : null;

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="modal wide" role="dialog" aria-modal="true" aria-label={task ? 'Edit task' : 'New task'}>
        <div className="modal-head">
          <h3>{task ? 'Edit task' : 'New task'}</h3>
          <div className="spacer" />
          {task && (
            <>
              <button type="button" className="btn sm" onClick={onTogglePin} disabled={saving}>
                {task.pinned ? '★ Unpin' : '☆ Pin to top'}
              </button>
              {onSnooze && (
                <>
                  <button type="button" className="btn sm" onClick={() => onSnooze(inDays(1))} disabled={saving}>
                    Snooze 1d
                  </button>
                  <button type="button" className="btn sm" onClick={() => onSnooze(inDays(7))} disabled={saving}>
                    1w
                  </button>
                  {task.snoozedUntil && (
                    <button type="button" className="btn sm" onClick={() => onSnooze(null)} disabled={saving}>
                      Wake
                    </button>
                  )}
                </>
              )}
              <span className="pill system-pill">{task.systemLabel}</span>
            </>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(draft);
          }}
        >
          <div className="modal-body form-grid">
            {task && <WhyPanel score={task.score} meta={meta} />}

            {task && task.nudges.length > 0 && (
              <div className="nudges">
                {task.nudges.map((n, i) => (
                  <div className="nudge" key={i}>
                    <span aria-hidden>◆</span>
                    <span>{n}</span>
                  </div>
                ))}
              </div>
            )}

            {isLinked && (
              <div className="notice info">
                <span>
                  Title, description, deadline, and status are written back to <strong>{task?.systemLabel}</strong>.
                  Everything under “Judgement” stays in Z3NTRAFY. Last synced {formatTimestamp(task?.lastSyncedAt ?? null)}.
                  Deleting below only removes it from this list — it's untouched in {task?.systemLabel}.
                </span>
              </div>
            )}
            {error && <div className="notice error">{error}</div>}
            {!!warnings.length && (
              <div className="notice warn">
                <div>
                  Saved, with caveats:
                  <ul>
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="form-row">
              <label htmlFor="task-title">Title</label>
              <input
                id="task-title"
                type="text"
                value={draft.title}
                onChange={(e) => set('title', e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="form-row">
              <label htmlFor="task-desc">Description</label>
              <textarea id="task-desc" value={draft.description} onChange={(e) => set('description', e.target.value)} />
            </div>

            <div className="two-col">
              <div className="form-row">
                <label htmlFor="task-due">Deadline</label>
                <input id="task-due" type="date" value={draft.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
              </div>
              <div className="form-row">
                <label htmlFor="task-status">Status</label>
                <select id="task-status" value={draft.status} onChange={(e) => set('status', e.target.value as Status)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                {isLinked && task?.sourceStatus && (
                  <span className="help">Currently “{task.sourceStatus}” in {task.systemLabel}.</span>
                )}
              </div>
            </div>

            {/* ------------------------------------------------ judgement */}
            <div className="section">
              <h4>Judgement — used for ranking, kept in Z3NTRAFY</h4>
              <div className="attr-grid">
                <div className="form-row">
                  <label htmlFor="a-importance">
                    Importance{derivedTag(task?.importanceSource ?? null)}
                  </label>
                  <select
                    id="a-importance"
                    value={draft.importance}
                    onChange={(e) => set('importance', e.target.value)}
                  >
                    <option value="">Not set</option>
                    {meta?.importance.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className="help">How consequential, regardless of timing.</span>
                </div>

                <div className="form-row">
                  <label htmlFor="a-effort">Effort{derivedTag(task?.effortSource ?? null)}</label>
                  <select id="a-effort" value={draft.effort} onChange={(e) => set('effort', e.target.value)}>
                    <option value="">Not set</option>
                    {meta?.efforts.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className="help">Small things score a quick-win bonus.</span>
                </div>

                <div className="form-row">
                  <label htmlFor="a-urgency">Urgency</label>
                  <select
                    id="a-urgency"
                    value={draft.urgencyOverride}
                    onChange={(e) => set('urgencyOverride', e.target.value)}
                  >
                    <option value="">
                      From deadline
                      {task ? ` — ${meta?.urgency.find((u) => u.id === task.urgency)?.label ?? task.urgency}` : ''}
                    </option>
                    {meta?.urgency.map((o) => (
                      <option key={o.id} value={o.id}>
                        Override: {o.label}
                      </option>
                    ))}
                  </select>
                  <span className="help">Override only when the date does not tell the whole story.</span>
                </div>

                <div className="form-row">
                  <label htmlFor="a-impact-type">Impact{derivedTag(task?.impactSource ?? null)}</label>
                  <select id="a-impact-type" value={draft.impactType} onChange={(e) => set('impactType', e.target.value)}>
                    <option value="">Not set</option>
                    {meta?.impactTypes.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="a-impact-scope">Reach</label>
                  <select
                    id="a-impact-scope"
                    value={draft.impactScope}
                    onChange={(e) => set('impactScope', e.target.value)}
                  >
                    <option value="">Not set</option>
                    {meta?.scope.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <label htmlFor="a-project">Project</label>
                  <ProjectSelect
                    id="a-project"
                    projects={projects}
                    value={draft.projectId}
                    onChange={(id) => set('projectId', id)}
                    onCreateProject={onCreateProject}
                    leadingOptions={[{ value: '', label: 'None' }]}
                    projectLabel={(p) => `${p.name}${p.orgPriorityName ? ` — ${p.orgPriorityName}` : ''}`}
                  />
                  {task?.orgPriorityName && <span className="help">Counts toward “{task.orgPriorityName}”.</span>}
                </div>
              </div>
            </div>

            {/* ------------------------------------- dependencies & promises */}
            <div className="section">
              <h4>Who is affected</h4>
              <div className="form-grid">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={draft.blockingOthers}
                    onChange={(e) => set('blockingOthers', e.target.checked)}
                  />
                  Someone is waiting on me for this
                </label>
                {draft.blockingOthers && (
                  <div className="form-row">
                    <label htmlFor="a-blocking-note">Who, and for what</label>
                    <input
                      id="a-blocking-note"
                      type="text"
                      value={draft.blockingNote}
                      placeholder="e.g. Design cannot start until this lands"
                      onChange={(e) => set('blockingNote', e.target.value)}
                    />
                  </div>
                )}
                <div className="two-col">
                  <div className="form-row">
                    <label htmlFor="a-commit-to">Promised to</label>
                    <input
                      id="a-commit-to"
                      type="text"
                      value={draft.commitmentTo}
                      placeholder="Nobody"
                      onChange={(e) => set('commitmentTo', e.target.value)}
                    />
                    {task?.commitmentMadeAt && (
                      <span className="help">Recorded {formatTimestamp(task.commitmentMadeAt)}.</span>
                    )}
                  </div>
                  <div className="form-row">
                    <label htmlFor="a-commit-due">By when</label>
                    <input
                      id="a-commit-due"
                      type="date"
                      value={draft.commitmentDue}
                      onChange={(e) => set('commitmentDue', e.target.value)}
                    />
                    <span className="help">Used for urgency if it is sooner than the deadline.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ------------------------------------------------------ links */}
            {task && (
              <div className="section">
                <h4>Related work</h4>
                {task.links.length > 0 ? (
                  <div className="link-list">
                    {task.links.map((l) => (
                      <div className="link-row" key={`${l.id}-${l.direction}`}>
                        <span className="rel">This {LINK_VERBS[l.type][l.direction]}</span>
                        <span className="t">{l.otherTitle}</span>
                        <span className="pill system-pill">{l.otherSystem}</span>
                        <span className={`pill ${l.otherStatus}`}>{STATUS_LABELS[l.otherStatus]}</span>
                        {l.syncEnabled && (
                          <span className="pill sync-pill" title="Editing either task's title, description, or status updates the other.">
                            Synced
                          </span>
                        )}
                        {onRemoveLink && (
                          <button type="button" className="btn ghost sm" onClick={() => onRemoveLink(l.id)}>
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="help">Nothing linked yet.</span>
                )}
                {onAddLink && (
                  <div className="link-add">
                    <select value={linkType} onChange={(e) => setLinkType(e.target.value as LinkType)}>
                      <option value="blocks">This blocks…</option>
                      <option value="relates_to">Relates to…</option>
                      <option value="duplicates">Duplicates…</option>
                    </select>
                    <select value={linkTarget} onChange={(e) => setLinkTarget(e.target.value)}>
                      <option value="">Pick a task…</option>
                      {candidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.systemLabel}: {c.title}
                        </option>
                      ))}
                    </select>
                    <label className="checkbox link-sync-check" title="Editing either task's title, description, or status will push the change to the other's source system.">
                      <input type="checkbox" checked={linkSync} onChange={(e) => setLinkSync(e.target.checked)} />
                      Keep in sync
                    </label>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={!linkTarget}
                      onClick={() => {
                        onAddLink(linkTarget, linkType, linkSync);
                        setLinkTarget('');
                        setLinkSync(false);
                      }}
                    >
                      Link
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* --------------------------------------------------------- documents */}
            {task && (
              <div className="section">
                <h4>Documents</h4>
                {task.artifacts.length > 0 ? (
                  <div className="link-list">
                    {task.artifacts.map((a) => (
                      <div className="link-row" key={a.id}>
                        <span className="pill system-pill">{a.providerLabel}</span>
                        <span className="t">{a.title}</span>
                        <a className="btn ghost sm" href={a.url} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="help">No documents created from this task yet.</span>
                )}
                {onCreateArtifact &&
                  (docProviders.length > 0 ? (
                    <div className="link-add">
                      <select
                        value={docProviderChoice}
                        onChange={(e) => setDocProviderChoice(e.target.value as ActionProviderId)}
                      >
                        {docProviders.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={shareWithOrg}
                          onChange={(e) => setShareWithOrg(e.target.checked)}
                        />
                        Share with org
                      </label>
                      <button
                        type="button"
                        className="btn sm"
                        disabled={saving || !docProviderChoice}
                        onClick={() => docProviderChoice && onCreateArtifact(docProviderChoice, shareWithOrg)}
                      >
                        Create doc
                      </button>
                    </div>
                  ) : (
                    <span className="help">
                      Connect a document provider under <strong>Integrations</strong> to create docs from tasks.
                    </span>
                  ))}
              </div>
            )}
          </div>

          <div className="modal-foot">
            {task && onDelete && (
              <>
                <button type="button" className="btn danger" onClick={onDelete} disabled={saving}>
                  Delete
                </button>
                <div className="spacer" />
              </>
            )}
            <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={saving || !draft.title.trim()}>
              {saving && <span className="spin" />}
              {isLinked ? 'Save & update source' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
